import { desc, type SQL, sql } from "drizzle-orm"
import {
  type AnyPgColumn,
  boolean,
  foreignKey,
  index,
  integer,
  jsonb,
  pgEnum,
  pgPolicy,
  pgRole,
  pgTable,
  real,
  text,
  timestamp,
  unique,
  uniqueIndex,
  varchar,
} from "drizzle-orm/pg-core"

const authenticatedRole = pgRole("authenticated")

export function lower(email: AnyPgColumn): SQL {
  return sql`lower(${email})`
}

function userId<T>(c: T) {
  return sql`((SELECT auth.uid() AS user_id) = ${c}::uuid)`
}

function publicPolicy(name: string) {
  return pgPolicy(name, {
    for: "all",
    as: "permissive",
    to: authenticatedRole,
    using: sql<boolean>`true`,
  })
}

export const accrualMethodEnum = [
  "ImportedRaindrop",
  "ImportedPocket",
  "ImportedHtml",
  "Direct",
] as const
export type AccrualMethods = (typeof accrualMethodEnum)[number]
export const accrualMethodPgEnum = pgEnum("accrualmethod", accrualMethodEnum)

export const signupSourceEnum = ["writer", "reader"] as const
export type SignupSources = (typeof signupSourceEnum)[number]
export const signupSourcePgEnum = pgEnum("signupsource", signupSourceEnum)

export const logStatusEnum = ["Success", "Failed", "Progress", "Poke"] as const
export type LogStatuses = (typeof logStatusEnum)[number]
export const logStatusPgEnum = pgEnum("status", logStatusEnum)

export const onboardingStages = [
  "set_username",
  "post_content",
  "preview_publication",
  "completed",
] as const
export type OnboardingStages = (typeof onboardingStages)[number]
export const onboardingStagesPgEnum = pgEnum(
  "onboarding_stage",
  onboardingStages,
)

export const onboardingTaskStatus = ["pending", "completed", "skipped"] as const
export type OnboardingTaskStatus = (typeof onboardingTaskStatus)[number]
export const onboardingTaskStatusPgEnum = pgEnum(
  "onboarding_task_status",
  onboardingTaskStatus,
)

export const emailImportStatusEnum = [
  "imported",
  "confirmed",
  "validated",
  "errored",
] as const
export type EmailImportStatus = (typeof emailImportStatusEnum)[number]
export const emailImportStatusPgEnum = pgEnum(
  "email_import_status",
  emailImportStatusEnum,
)

export const publicationImportStatusEnum = [
  "fetching_urls",
  "ready_to_import",
  "importing",
  "completed",
  "errored",
] as const
export type PublicationImportStatus =
  (typeof publicationImportStatusEnum)[number]
export const publicationImportStatusPgEnum = pgEnum(
  "publication_import_status",
  publicationImportStatusEnum,
)

export const publicationImportSourceKindEnum = ["url", "upload"] as const
export type PublicationImportSourceKind =
  (typeof publicationImportSourceKindEnum)[number]
export const publicationImportSourceKindPgEnum = pgEnum(
  "publication_import_source_kind",
  publicationImportSourceKindEnum,
)

export const subscriptionStatusEnum = ["subscribed", "imported"] as const
export type SubscriptionStatus = (typeof subscriptionStatusEnum)[number]
export const subscriptionStatusPgEnum = pgEnum(
  "subscription_status",
  subscriptionStatusEnum,
)

const base = {
  id: varchar().primaryKey().notNull(),
  created_at: timestamp("created_at", {
    withTimezone: true,
  })
    .defaultNow()
    .notNull(),
  updated_at: timestamp("updated_at", { withTimezone: true })
    .defaultNow()
    .notNull()
    .$onUpdate(() => new Date()),
}

export const users = pgTable(
  "users",
  {
    ...base,
    email: varchar().notNull(),
    emailVerified: boolean("email_verified").default(false).notNull(),
    name: varchar(),
    image: varchar(),
    title: varchar(),
    description: varchar(),
    blog_footer: varchar(),
    blog_header: varchar(),
    logo_url: varchar("logo_url"),
    profile_image: varchar("profile_image"),
    wordmark_url: varchar("wordmark_url"),
    welcome_image_url: varchar("welcome_image_url"),
    favicon_url: varchar("favicon_url"),
    last_sign_in_at: timestamp("last_sign_in_at", {
      withTimezone: true,
    }).notNull(),
    // TODO: eventually this should be called writer_onboarded
    // and be a boolean
    onboarding_stage: onboardingStagesPgEnum(),
    signup_source: signupSourcePgEnum().default("writer"),
    locale: varchar("locale").default("en").notNull(),
    interface_locale: varchar("interface_locale").default("en").notNull(),
    author_name: varchar("author_name"),
    bio: varchar("bio"),
    social_x: varchar("social_x"),
    social_instagram: varchar("social_instagram"),
    social_tiktok: varchar("social_tiktok"),
    social_youtube: varchar("social_youtube"),
    social_facebook: varchar("social_facebook"),
    social_linkedin: varchar("social_linkedin"),
    social_bluesky: varchar("social_bluesky"),
    social_books: varchar("social_books"),
    social_website: varchar("social_website"),
    social_soundcloud: varchar("social_soundcloud"),
    social_bandcamp: varchar("social_bandcamp"),
    suggested_tags: varchar("suggested_tags"),
    suggested_categories: varchar("suggested_categories"),
    summary: varchar("summary"),
    spam_score: integer("spam_score"),
    disabled_at: timestamp("disabled_at", { withTimezone: true }),
    suggested_language: varchar("suggested_language"),
    reader_onboarded: boolean("reader_onboarded").default(false).notNull(),
    interest_categories: varchar("interest_categories"),
    marketing_emails_unsubscribed: boolean("marketing_emails_unsubscribed")
      .default(false)
      .notNull(),
    weekly_featured_posts_unsubscribed: boolean(
      "weekly_featured_posts_unsubscribed",
    )
      .default(false)
      .notNull(),
    additional_languages: jsonb("additional_languages").$type<string[]>(),
    flagged_message_count: integer("flagged_message_count")
      .notNull()
      .default(0),
    is_admin: boolean("is_admin").default(false).notNull(),
    previous_name: varchar("previous_name"),
    name_changed_at: timestamp("name_changed_at", { withTimezone: true }),
  },
  t => [
    pgPolicy("users_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.id),
      withCheck: userId(t.id),
    }),
    index("ix__users__created_at").on(t.created_at),
    uniqueIndex("ix__users__email").on(lower(t.email)),
    uniqueIndex("ix__users__name").on(lower(t.name)),
    index("ix__users__disabled_at").on(t.disabled_at),
    index("ix__users__previous_name").on(lower(t.previous_name)),
  ],
)

export const sessions = pgTable(
  "sessions",
  {
    id: text("id").primaryKey(),
    expiresAt: timestamp("expires_at").notNull(),
    token: text("token").notNull().unique(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .$onUpdate(() => new Date())
      .notNull(),
    ipAddress: text("ip_address"),
    userAgent: text("user_agent"),
    userId: text("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
  },
  t => [
    pgPolicy("sessions_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.userId),
      withCheck: userId(t.userId),
    }),
  ],
)

export const accounts = pgTable(
  "accounts",
  {
    id: text("id").primaryKey(),
    accountId: text("account_id").notNull(),
    providerId: text("provider_id").notNull(),
    userId: text("user_id")
      .notNull()
      .references(() => users.id, { onDelete: "cascade" }),
    accessToken: text("access_token"),
    refreshToken: text("refresh_token"),
    idToken: text("id_token"),
    accessTokenExpiresAt: timestamp("access_token_expires_at"),
    refreshTokenExpiresAt: timestamp("refresh_token_expires_at"),
    scope: text("scope"),
    password: text("password"),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .$onUpdate(() => new Date())
      .notNull(),
  },
  t => [
    pgPolicy("accounts_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.userId),
      withCheck: userId(t.userId),
    }),
  ],
)

export const verifications = pgTable(
  "verifications",
  {
    id: text("id").primaryKey(),
    identifier: text("identifier").notNull(),
    value: text("value").notNull(),
    expiresAt: timestamp("expires_at").notNull(),
    createdAt: timestamp("created_at").defaultNow().notNull(),
    updatedAt: timestamp("updated_at")
      .defaultNow()
      .$onUpdate(() => new Date())
      .notNull(),
  },
  _t => [
    pgPolicy("verifications_service_role_only", {
      for: "all",
      to: authenticatedRole,
      using: sql<boolean>`false`,
      withCheck: sql<boolean>`false`,
    }),
  ],
)

export const subscriptions = pgTable(
  "subscriptions",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    publisher_id: varchar("publisher_id").notNull(),
    post_id: varchar("post_id"),
    silenced: boolean().default(false).notNull(),
    tier_id: varchar("tier_id"),
    form_id: varchar("form_id"),
    stripe_subscription_id: varchar("stripe_subscription_id"),
    stripe_customer_id: varchar("stripe_customer_id"),
    current_period_end: timestamp("current_period_end", {
      withTimezone: true,
    }),
    billing_interval: varchar("billing_interval", { length: 10 }),
    status: subscriptionStatusPgEnum().default("subscribed").notNull(),
  },
  t => [
    pgPolicy("subscriptions_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__subscriptions__publisher_id").on(t.publisher_id),
    index("ix__subscriptions__user_id").on(t.user_id),
    index("ix__subscriptions__tier_id").on(t.tier_id),
    index("ix__subscriptions__form_id").on(t.form_id),
    unique("ix__subscriptions__user_id_publisher_id").on(
      t.user_id,
      t.publisher_id,
    ),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__subscriptions__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__subscriptions__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__subscriptions__post_id__posts",
    }).onDelete("set null"),
    foreignKey({
      columns: [t.tier_id],
      foreignColumns: [subscriptionTiers.id],
      name: "fk__subscriptions__tier_id__subscription_tiers",
    }).onDelete("set null"),
    foreignKey({
      columns: [t.form_id],
      foreignColumns: [leadMagnetForms.id],
      name: "fk__subscriptions__form_id__lead_magnet_forms",
    }).onDelete("set null"),
  ],
)

export const paymentStatusEnum = [
  "pending",
  "processing",
  "succeeded",
  "failed",
] as const
export type PaymentStatus = (typeof paymentStatusEnum)[number]
export const paymentStatusPgEnum = pgEnum("payment_status", paymentStatusEnum)

export const userPayments = pgTable(
  "user_payments",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    stripe_checkout_session_id: varchar("stripe_checkout_session_id").notNull(),
    stripe_subscription_id: varchar("stripe_subscription_id"),
    stripe_customer_id: varchar("stripe_customer_id"),
    tier_id: varchar("tier_id").notNull(),
    publisher_id: varchar("publisher_id").notNull(),
    status: paymentStatusPgEnum().default("pending").notNull(),
  },
  t => [
    pgPolicy("user_payments_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__user_payments__user_id").on(t.user_id),
    index("ix__user_payments__stripe_checkout_session_id").on(
      t.stripe_checkout_session_id,
    ),
    index("ix__user_payments__status").on(t.status),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__user_payments__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__user_payments__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.tier_id],
      foreignColumns: [subscriptionTiers.id],
      name: "fk__user_payments__tier_id__subscription_tiers",
    }).onDelete("cascade"),
  ],
)

export const stories = pgTable(
  "stories",
  {
    ...base,
    url: varchar().notNull(),
    metadated_at: timestamp("metadated_at", {
      withTimezone: true,
    }),
    feed_url: varchar("feed_url"),
    dc_created_at: timestamp("dc_created_at", {
      withTimezone: true,
    }),
    dc_creator: varchar("dc_creator"),
    dc_description: varchar("dc_description"),
    dc_language: varchar("dc_language"),
    dc_modified_at: timestamp("dc_modified_at", {
      withTimezone: true,
    }),
    dc_title: varchar("dc_title"),
    dc_identifier: varchar("dc_identifier"),
    truncated_body: varchar("truncated_body"),
    metadated_attempts: integer("metadated_attempts").default(0).notNull(),
    summary: varchar("summary"),
    tags: varchar("tags"),
    image_url: varchar("image_url"),
    logo_url: varchar("logo_url"),
  },
  t => [
    publicPolicy("stories_authenticated"),
    index("ix__stories__dc_created_at").on(t.dc_created_at),
    index("ix__stories__feed_url").on(t.feed_url),
    unique("ix__stories__url").on(t.url),
    index("ix__stories__metadated_at").on(t.metadated_at),
    index("ix__stories__metadated_attempts").on(t.metadated_attempts),
    index("ix__stories__created_at").on(t.created_at),
  ],
)

export const publishers = pgTable(
  "publishers",
  {
    ...base,
    url: varchar().notNull(),
    user_id: varchar("user_id").notNull(),
    metadated_at: timestamp("metadated_at", {
      withTimezone: true,
    }),
    dc_description: varchar("dc_description"),
    dc_title: varchar("dc_title"),
    dc_language: varchar("dc_language"),
    dc_creator: varchar("dc_creator"),
    dc_created_at: timestamp("dc_created_at", {
      withTimezone: true,
    }),
    dc_modified_at: timestamp("dc_modified_at", {
      withTimezone: true,
    }),
    dc_identifier: varchar("dc_identifier"),
    feed_etag: varchar("feed_etag"),
    feed_last_modified: varchar("feed_last_modified"),
    feed_next_polling_at: timestamp("feed_next_polling_at", {
      withTimezone: true,
    }),
    metadated_attempts: integer("metadated_attempts").default(0).notNull(),
    image_url: varchar("image_url"),
    logo_url: varchar("logo_url"),
    welcome_email_subject: varchar("welcome_email_subject"),
    welcome_email_content: text("welcome_email_content"),
    tags: varchar("tags"),
    polling_attempts: integer("polling_attempts").default(0).notNull(),
    stripe_account_id: varchar("stripe_account_id"),
    stripe_country: varchar("stripe_country"),
    stripe_onboarding_completed: boolean("stripe_onboarding_completed")
      .default(false)
      .notNull(),
    stripe_business_name: varchar("stripe_business_name"),
    stripe_company_name: varchar("stripe_company_name"),
    stripe_icon_url: varchar("stripe_icon_url"),
    enable_monetization: boolean("enable_monetization")
      .default(false)
      .notNull(),
    reaction_emojis: varchar("reaction_emojis").default("❤️,🔥,👏,🤔,😮,😂"),
    opt_out_featured_posts: boolean("opt_out_featured_posts")
      .default(false)
      .notNull(),
    fee_overrides: jsonb("fee_overrides").$type<[number, number][]>(),
  },
  t => [
    publicPolicy("publishers_authenticated"),
    index("ix__publishers__feed_next_polling_at").on(t.feed_next_polling_at),
    index("ix__publishers__user_id").on(t.user_id),
    unique("ix__publishers__url").on(t.url),
    unique("ix__users__user_id").on(t.user_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__publishers__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const subscriptionTiers = pgTable(
  "subscription_tiers",
  {
    ...base,
    publisher_id: varchar("publisher_id").notNull(),
    user_id: varchar("user_id").notNull(),
    name: varchar().notNull(),
    description: varchar(),
    monthly_price_cents: integer("monthly_price_cents").notNull(),
    annual_price_cents: integer("annual_price_cents"),
    stripe_product_id: varchar("stripe_product_id"),
    stripe_monthly_price_id: varchar("stripe_monthly_price_id"),
    stripe_annual_price_id: varchar("stripe_annual_price_id"),
  },
  t => [
    pgPolicy("subscription_tiers_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__subscription_tiers__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__subscription_tiers__user_id__users",
    }).onDelete("cascade"),
    index("ix__subscription_tiers__publisher_id").on(t.publisher_id),
    index("ix__subscription_tiers__user_id").on(t.user_id),
  ],
)

export const leadMagnetForms = pgTable(
  "lead_magnet_forms",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    name: varchar().notNull(),
    content: text("content"),
    email_subject: varchar("email_subject").notNull(),
    email_body: text("email_body"),
    private: boolean("private").notNull().default(false),
    tier_id: varchar("tier_id"),
  },
  t => [
    pgPolicy("lead_magnet_forms_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__lead_magnet_forms__user_id").on(t.user_id),
    index("ix__lead_magnet_forms__tier_id").on(t.tier_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__lead_magnet_forms__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.tier_id],
      foreignColumns: [subscriptionTiers.id],
      name: "fk__lead_magnet_forms__tier_id__subscription_tiers",
    }).onDelete("set null"),
  ],
)

export const leadMagnetFiles = pgTable(
  "lead_magnet_files",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    form_id: varchar("form_id").notNull(),
    src: varchar().notNull(),
    title: varchar().notNull(),
  },
  t => [
    pgPolicy("lead_magnet_files_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__lead_magnet_files__user_id").on(t.user_id),
    index("ix__lead_magnet_files__form_id").on(t.form_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__lead_magnet_files__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.form_id],
      foreignColumns: [leadMagnetForms.id],
      name: "fk__lead_magnet_files__form_id__lead_magnet_forms",
    }).onDelete("cascade"),
  ],
)

export const postTiers = pgTable(
  "post_tiers",
  {
    post_id: varchar("post_id").notNull(),
    tier_id: varchar("tier_id").notNull(),
  },
  t => [
    pgPolicy("post_tiers_private", {
      for: "all",
      to: authenticatedRole,
      using: sql`EXISTS (SELECT 1 FROM posts WHERE posts.id = ${t.post_id} AND (SELECT auth.uid()) = posts.user_id::uuid)`,
      withCheck: sql`EXISTS (SELECT 1 FROM posts WHERE posts.id = ${t.post_id} AND (SELECT auth.uid()) = posts.user_id::uuid)`,
    }),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_tiers__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.tier_id],
      foreignColumns: [subscriptionTiers.id],
      name: "fk__post_tiers__tier_id__subscription_tiers",
    }).onDelete("cascade"),
    index("ix__post_tiers__post_id").on(t.post_id),
    index("ix__post_tiers__post_id_tier_id").on(t.post_id, t.tier_id),
  ],
)

export const bookmarks = pgTable(
  "bookmarks",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    story_id: varchar("story_id").notNull(),
    bookmarked_at: timestamp("bookmarked_at", {
      withTimezone: true,
    }).notNull(),
    dc_accrual_method: accrualMethodPgEnum("dc_accrual_method"),
  },
  t => [
    pgPolicy("bookmarks_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__bookmarks__story_id").on(t.story_id),
    index("ix__bookmarks__user_id").on(t.user_id),
    unique("ix__bookmarks__user_id_story_id").on(t.user_id, t.story_id),
    foreignKey({
      columns: [t.story_id],
      foreignColumns: [stories.id],
      name: "fk__bookmarks__story_id__stories",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__bookmarks__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const bookmarkTags = pgTable(
  "bookmark_tags",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    bookmark_id: varchar("bookmark_id").notNull(),
    tag: varchar().notNull(),
  },
  t => [
    pgPolicy("bookmark_tags_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__bookmark_tags__bookmark_id").on(t.bookmark_id),
    index("ix__bookmark_tags__user_id").on(t.user_id),
    unique("ix__bookmark_tags__bookmark_id_tag").on(t.bookmark_id, t.tag),
    foreignKey({
      columns: [t.bookmark_id],
      foreignColumns: [bookmarks.id],
      name: "fk__bookmark_tags__bookmark_id__bookmarks",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__bookmark_tags__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const userNoteTags = pgTable(
  "user_note_tags",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    user_note_id: varchar("user_note_id").notNull(),
    tag: varchar().notNull(),
  },
  t => [
    pgPolicy("user_note_tags_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__user_note_tags__user_note_id").on(t.user_note_id),
    index("ix__user_note_tags__user_id").on(t.user_id),
    unique("ix__user_note_tags__user_note_id_tag").on(t.user_note_id, t.tag),
    foreignKey({
      columns: [t.user_note_id],
      foreignColumns: [userNotes.id],
      name: "fk__note_tags__note_id__user_notes",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__note_tags__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const postTags = pgTable(
  "post_tags",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    post_id: varchar("post_id").notNull(),
    tag: varchar().notNull(),
  },
  t => [
    pgPolicy("post_tags_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__post_tags__post_id").on(t.post_id),
    index("ix__post_tags__user_id").on(t.user_id),
    unique("ix__post_tags__post_id_tag").on(t.post_id, t.tag),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_tags__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__post_tags__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const logs = pgTable(
  "logs",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    title: varchar().notNull(),
    status: logStatusPgEnum(),
  },
  t => [
    pgPolicy("logs_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__logs__user_id").on(t.user_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__logs__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const posts = pgTable(
  "posts",
  {
    ...base,
    canonical_url: varchar("canonical_url"),
    bookmark_id: varchar("bookmark_id"),
    user_id: varchar("user_id").notNull(),
    pinned_at: timestamp("pinned_at", { withTimezone: true }),
    published_at: timestamp("published_at", { withTimezone: true }),
    first_published_at: timestamp("first_published_at", { withTimezone: true }),
    sent_at: timestamp("sent_at", { withTimezone: true }),
    sent_version_id: varchar("sent_version_id"),
    archived_at: timestamp("archived_at", { withTimezone: true }),
    archived_version_id: varchar("archived_version_id"),
    spam_score: integer("spam_score"),
    main_locale: varchar("main_locale").notNull().default("en"),
    last_edit_at: timestamp("last_edit_at", { withTimezone: true }).default(
      sql`now()`,
    ),
    suggested_tags: varchar("suggested_tags"),
    summary: varchar("summary"),
    form_id: varchar("form_id"),
    suggested_language: varchar("suggested_language"),
    used_proofreader_at: timestamp("used_proofreader_at", {
      withTimezone: true,
    }),
    private: boolean("private").notNull().default(false),
    comments_enabled: boolean("comments_enabled").notNull().default(true),
    featured_at: timestamp("featured_at", { withTimezone: true }),
    human_content_score: real("human_content_score"),
    human_content_raw_score: real("human_content_raw_score"),
    human_content_edits_count: integer("human_content_edits_count"),
  },
  t => [
    pgPolicy("posts_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__posts__user_id").on(t.user_id),
    index("ix__posts__user_id_published_at").on(t.user_id, t.published_at),
    index("ix__posts__bookmark_id").on(t.bookmark_id),
    index("ix__posts__form_id").on(t.form_id),
    index("ix__posts__featured_at")
      .on(t.featured_at)
      .where(sql`"featured_at" IS NOT NULL`),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__posts__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.bookmark_id],
      foreignColumns: [bookmarks.id],
      name: "fk__posts__bookmark_id__bookmarks",
    }).onDelete("set null"),
    foreignKey({
      columns: [t.form_id],
      foreignColumns: [leadMagnetForms.id],
      name: "fk__posts__form_id__lead_magnet_forms",
    }).onDelete("set null"),
  ],
)

export const postsVersions = pgTable(
  "post_versions",
  {
    ...base,
    post_id: varchar("post_id").notNull(),
    title: varchar().notNull(),
    description: varchar(),
    content: varchar().notNull(),
    image_header_url: varchar("image_header_url"),
    social_image_url: varchar("social_image_url"),
    user_id: varchar("user_id").notNull(),
    locale: varchar("locale").notNull().default("en"),
    published: boolean("published").default(false).notNull(),
    sent: boolean("sent").default(false).notNull(),
    meta_title: varchar("meta_title"),
    meta_description: varchar("meta_description"),
    translated: boolean("translated").default(false).notNull(),
  },
  t => [
    pgPolicy("post_versions_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__post_versions__user_id").on(t.user_id),
    index("ix__post_versions__post_id").on(t.post_id),
    index("ix__post_versions__post_id_published").on(t.post_id, t.published),
    index("ix__post_versions__post_id_updated_at").on(
      t.post_id,
      desc(t.updated_at),
    ),
    index("ix__post_versions__post_id_locale_updated_at").on(
      t.post_id,
      t.locale,
      desc(t.updated_at),
    ),
    uniqueIndex("uq__post_versions__post_id_locale_published")
      .on(t.post_id, t.locale)
      .where(sql`${t.published} = true`),
    uniqueIndex("uq__post_versions__post_id_locale_sent")
      .on(t.post_id, t.locale)
      .where(sql`${t.sent} = true`),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__post_versions__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_versions__post_id__posts",
    }).onDelete("cascade"),
  ],
)

export const noteTypes = ["voice", "user"] as const
export type NoteType = (typeof noteTypes)[number]
export const noteTypePgEnum = pgEnum("note_type", noteTypes)

export const insightTypes = ["insight", "question", "phrase", "idea"] as const
export type InsightType = (typeof insightTypes)[number]
export const insightTypePgEnum = pgEnum("insight_type", insightTypes)

export const userNotes = pgTable(
  "user_notes",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    content: varchar().notNull(),
    type: noteTypePgEnum().default("user").notNull(),
  },
  t => [
    pgPolicy("user_notes_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__user_notes__user_id").on(t.user_id),
    index("ix__user_notes__type").on(t.type),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__user_notes__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const insights = pgTable(
  "insights",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    content: varchar().notNull(),
    insight_type: insightTypePgEnum().notNull(),
    source_note_id: varchar("source_note_id").notNull(),
  },
  t => [
    pgPolicy("insights_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__insights__user_id").on(t.user_id),
    index("ix__insights__source_note_id").on(t.source_note_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__insights__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.source_note_id],
      foreignColumns: [userNotes.id],
      name: "fk__insights__source_note_id__user_notes",
    }).onDelete("cascade"),
  ],
)

export const postNotes = pgTable(
  "post_notes",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    post_id: varchar("post_id").notNull(),
    note_id: varchar("note_id").notNull(),
  },
  t => [
    pgPolicy("post_notes_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__post_notes__user_id").on(t.user_id),
    index("ix__post_notes__post_id").on(t.post_id),
    index("ix__post_notes__note_id").on(t.note_id),
    unique("ix__post_notes__post_id_note_id").on(t.post_id, t.note_id),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__post_notes__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_notes__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.note_id],
      foreignColumns: [userNotes.id],
      name: "fk__post_notes__note_id__user_notes",
    }).onDelete("cascade"),
  ],
)

export const postBookmarks = pgTable(
  "post_bookmarks",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    post_id: varchar("post_id").notNull(),
    bookmark_id: varchar("bookmark_id").notNull(),
  },
  t => [
    pgPolicy("post_bookmarks_private", {
      for: "all",
      to: authenticatedRole,
      withCheck: userId(t.user_id),
      using: userId(t.user_id),
    }),
    index("ix__post_bookmarks__user_id").on(t.user_id),
    index("ix__post_bookmarks__post_id").on(t.post_id),
    index("ix__post_bookmarks__bookmark_id").on(t.bookmark_id),
    unique("ix__post_bookmarks__post_id_bookmark_id").on(
      t.post_id,
      t.bookmark_id,
    ),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__post_bookmarks__user_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_bookmarks__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.bookmark_id],
      foreignColumns: [bookmarks.id],
      name: "fk__post_bookmarks__bookmark_id__bookmarks",
    }).onDelete("cascade"),
  ],
)

export const publicationExports = pgTable(
  "publication_exports",
  {
    ...base,
    publication_id: varchar("publication_id").notNull(),
    num_posts: integer("num_posts"),
    num_subscribers: integer("num_subscribers"),
    completed_at: timestamp("completed_at", { withTimezone: true }),
    error: varchar("error"),
  },
  t => [
    pgPolicy("publication_exports_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.publication_id),
      withCheck: userId(t.publication_id),
    }),
    index("ix__publication_exports__publication_id").on(t.publication_id),
    foreignKey({
      columns: [t.publication_id],
      foreignColumns: [users.id],
      name: "fk__publication_exports__publication_id__users",
    }).onDelete("cascade"),
  ],
)

export interface PublicationImportDiscoveredUrlItem {
  type: "url"
  url: string
  title: string | null
  image: string | null
  description: string | null
  publishedAt: string | null
  discoveryMethod: string | null
}

export interface PublicationImportDiscoveredFileItem {
  type: "file"
  path: string
  r2Key: string
  title: string | null
  mimeType: string | null
  publishedAt: string | null
}

export type PublicationImportDiscoveredItem =
  | PublicationImportDiscoveredUrlItem
  | PublicationImportDiscoveredFileItem

export interface PublicationImportSourceUpload {
  fileName: string
  mimeType: string | null
  sourceR2Key: string
}

export interface PublicationImportProcessedUpload {
  fileName: string
  mimeType: string | null
  r2Key: string
  fileIndex: Record<string, string>
}

export type PublicationImportUploadPayload =
  | PublicationImportSourceUpload
  | PublicationImportProcessedUpload

export const publicationImportPostStatusEnum = [
  "pending",
  "imported",
  "errored",
] as const
export type PublicationImportPostStatus =
  (typeof publicationImportPostStatusEnum)[number]

export interface PublicationImportPostProgressItem {
  sourceType: "url" | "file"
  sourceIdentifier: string
  postId: string | null
  title: string | null
  status: PublicationImportPostStatus
  error: string | null
  firstPublishedAt: string | null
}

export const publicationImports = pgTable(
  "publication_imports",
  {
    ...base,
    publication_id: varchar("publication_id").notNull(),
    user_id: varchar("user_id").notNull(),
    source_kind: publicationImportSourceKindPgEnum("source_kind").notNull(),
    source_url: text("source_url"),
    source_upload:
      jsonb("source_upload").$type<PublicationImportUploadPayload>(),
    status: publicationImportStatusPgEnum().default("fetching_urls").notNull(),
    discovered_items: jsonb("discovered_items")
      .$type<PublicationImportDiscoveredItem[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    imported_posts: jsonb("imported_posts")
      .$type<PublicationImportPostProgressItem[]>()
      .default(sql`'[]'::jsonb`)
      .notNull(),
    started_at: timestamp("started_at", { withTimezone: true }),
    completed_at: timestamp("completed_at", { withTimezone: true }),
    error: varchar("error"),
    discovery_stage: text("discovery_stage"),
  },
  t => [
    pgPolicy("publication_imports_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.user_id),
      withCheck: userId(t.user_id),
    }),
    index("ix__publication_imports__publication_id").on(t.publication_id),
    index("ix__publication_imports__user_id").on(t.user_id),
    index("ix__publication_imports__status").on(t.status),
    foreignKey({
      columns: [t.publication_id],
      foreignColumns: [users.id],
      name: "fk__publication_imports__publication_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__publication_imports__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const onboardingTask = pgTable(
  "onboarding_task",
  {
    ...base,
    user_id: varchar("user_id").notNull(),
    task_key: text("task_key").notNull(),
    status: onboardingTaskStatusPgEnum().default("pending").notNull(),
    completed_at: timestamp("completed_at", { withTimezone: true }),
    email_sent_at: timestamp("email_sent_at", { withTimezone: true }),
    last_evaluated_at: timestamp("last_evaluated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  t => [
    unique("ix__onboarding_task__user_id_task_key").on(t.user_id, t.task_key),
    pgPolicy("onboarding_task_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.user_id),
      withCheck: userId(t.user_id),
    }),
    index("ix__onboarding_task__user_id").on(t.user_id),
    index("ix__onboarding_task__user_id_status").on(t.user_id, t.status),
    index("ix__onboarding_task__task_key").on(t.task_key),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__onboarding_task__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const emailImports = pgTable(
  "email_imports",
  {
    ...base,
    publisher_id: varchar("publisher_id").notNull(),
    user_id: varchar("user_id").notNull(),
    uploaded_emails: integer("uploaded_emails").notNull(),
    imported_emails: integer("imported_emails").notNull(),
    emails: text("emails").notNull(),
    status: emailImportStatusPgEnum().default("imported").notNull(),
    confirmed_at: timestamp("confirmed_at", { withTimezone: true }),
    validated_at: timestamp("validated_at", { withTimezone: true }),
    error: varchar("error"),
    origin: text("origin").notNull(),
  },
  t => [
    pgPolicy("email_imports_private", {
      for: "all",
      to: authenticatedRole,
      using: userId(t.user_id),
      withCheck: userId(t.user_id),
    }),
    index("ix__email_imports__status").on(t.status),
    index("ix__email_imports__user_id").on(t.user_id),
    index("ix__email_imports__publisher_id").on(t.publisher_id),
    foreignKey({
      columns: [t.publisher_id],
      foreignColumns: [publishers.id],
      name: "fk__email_imports__publisher_id__publishers",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__email_imports__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const postReactions = pgTable(
  "post_reactions",
  {
    ...base,
    post_id: varchar("post_id").notNull(),
    user_id: varchar("user_id").notNull(),
    emoji: varchar("emoji").notNull(),
  },
  t => [
    publicPolicy("post_reactions_authenticated"),
    index("ix__post_reactions__post_id").on(t.post_id),
    index("ix__post_reactions__user_id").on(t.user_id),
    index("ix__post_reactions__post_id_emoji").on(t.post_id, t.emoji),
    unique("ix__post_reactions__post_id_user_id").on(t.post_id, t.user_id),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__post_reactions__post_id__posts",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__post_reactions__user_id__users",
    }).onDelete("cascade"),
  ],
)

export const threads = pgTable(
  "threads",
  {
    id: varchar().primaryKey().notNull(),
    publication_id: varchar("publication_id").notNull(),
    post_id: varchar("post_id"),
    subject: text("subject"),
    private: boolean("private").default(true).notNull(),
    created_at: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updated_at: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  t => [
    publicPolicy("threads_authenticated"),
    index("ix__threads__publication_id").on(t.publication_id),
    index("ix__threads__post_id").on(t.post_id),
    index("ix__threads__updated_at").on(t.updated_at),
    foreignKey({
      columns: [t.publication_id],
      foreignColumns: [users.id],
      name: "fk__threads__publication_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.post_id],
      foreignColumns: [posts.id],
      name: "fk__threads__post_id__posts",
    }).onDelete("set null"),
  ],
)

export const messages = pgTable(
  "messages",
  {
    id: varchar().primaryKey().notNull(),
    author_id: varchar("author_id").notNull(),
    publication_id: varchar("publication_id").notNull(),
    thread_id: varchar("thread_id").notNull(),
    content: text("content").notNull(),
    locale: varchar("locale"),
    created_at: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  t => [
    publicPolicy("messages_authenticated"),
    index("ix__messages__thread_id__created_at").on(t.thread_id, t.created_at),
    index("ix__messages__author_id").on(t.author_id),
    foreignKey({
      columns: [t.author_id],
      foreignColumns: [users.id],
      name: "fk__messages__author_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.publication_id],
      foreignColumns: [users.id],
      name: "fk__messages__publication_id__users",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.thread_id],
      foreignColumns: [threads.id],
      name: "fk__messages__thread_id__threads",
    }).onDelete("cascade"),
  ],
)

export const messageReads = pgTable(
  "message_reads",
  {
    thread_id: varchar("thread_id").notNull(),
    user_id: varchar("user_id").notNull(),
    last_read_at: timestamp("last_read_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  t => [
    publicPolicy("message_reads_authenticated"),
    unique("ix__message_reads__thread_id_user_id").on(t.thread_id, t.user_id),
    foreignKey({
      columns: [t.thread_id],
      foreignColumns: [threads.id],
      name: "fk__message_reads__thread_id__threads",
    }).onDelete("cascade"),
    foreignKey({
      columns: [t.user_id],
      foreignColumns: [users.id],
      name: "fk__message_reads__user_id__users",
    }).onDelete("cascade"),
  ],
)

export type Story = typeof stories.$inferSelect
export type Publisher = typeof publishers.$inferSelect
export type Bookmark = typeof bookmarks.$inferSelect
export type BookmarkTag = typeof bookmarkTags.$inferSelect
export type PostTag = typeof postTags.$inferSelect
export type User = typeof users.$inferSelect
export type Log = typeof logs.$inferSelect
export type Post = typeof posts.$inferSelect
export type Subscription = typeof subscriptions.$inferSelect
export type SubscriptionTier = typeof subscriptionTiers.$inferSelect
export type UserNote = typeof userNotes.$inferSelect
export type PostNote = typeof postNotes.$inferSelect
export type PostBookmark = typeof postBookmarks.$inferSelect
export type OnboardingTask = typeof onboardingTask.$inferSelect
export type PublicationExport = typeof publicationExports.$inferSelect
export type PublicationImport = typeof publicationImports.$inferSelect
export type EmailImport = typeof emailImports.$inferSelect
export type PostReaction = typeof postReactions.$inferSelect
export type Thread = typeof threads.$inferSelect
export type Message = typeof messages.$inferSelect
export type MessageRead = typeof messageReads.$inferSelect
